import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { isEmpty, startCase } from 'lodash';
import { PRIZE_TYPES } from '@/constants';
import { PrizeDTO, PrizeRequestDTO, RewardItemDisplayDTO, SelectionOptions } from '@/models';
import PrizeCard from '@/components/pages/rewards/prize-card/PrizeCard.vue';
import blankImage from '@/assets/images/blankImage.png';

@Component({
  components: {
    PrizeCard
  }
})
export default class PrizeForm extends Vue {
  @Prop() prize: PrizeDTO;
  @Prop({ default: false }) loading: boolean;

  $refs!: {
    formRef: HTMLFormElement;
  };

  private prizeRequest: PrizeRequestDTO = {
    name: '',
    type: PRIZE_TYPES.BACKPACK,
    image: null,
    description: ''
  };
  private isPrizeHasImage = false;
  private previewImageURLTemp = '';
  private typeOptions: SelectionOptions = [];
  private ruleOptions = {
    name: [(v: string) => !!v || 'Prize name is required'],
    image: [() => this.isImageValid() || 'Image must be PNG, JPG or JPEG']
  };

  isImageValid(): boolean {
    const acceptedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    return !this.prizeRequest?.image || acceptedTypes.includes(this.prizeRequest.image.type);
  }

  @Watch('prize', { immediate: true })
  onPrizeChange(): void {
    this.prizeRequest = {
      ...this.prize,
      image: null
    };
    this.isPrizeHasImage = !isEmpty(this.prize.image) && typeof this.prize.image === 'string';
  }

  get newPrize(): RewardItemDisplayDTO {
    return {
      name: this.prizeRequest.name,
      image: this.previewImage,
      description: this.prizeRequest.description || '',
      type: this.prizeRequest.type,
      cost: 0,
      quantity: 0,
      redeemCount: 0,
      estimatedPrice: 0,
    }
  }

  get formTitle(): string {
    return this.prize.id ? 'Edit Prize' : 'Add Prize';
  }

  get previewImage(): string {
    if (this.isPrizeHasImage) {
      return this.prize.image;
    }
    if (this.prizeRequest?.image) {
      this.revokePreviewURL();
      this.previewImageURLTemp = URL.createObjectURL(this.prizeRequest.image);
      return this.previewImageURLTemp;
    }
    return blankImage;
  }

  revokePreviewURL(): void {
    if (!isEmpty(this.previewImageURLTemp)) {
      URL.revokeObjectURL(this.previewImageURLTemp);
    }
  }

  clearPreviewImage(): void {
    this.isPrizeHasImage = false;
  }

  resetForm(): void {
    this.$refs.formRef.reset();
  }

  formValidate(): boolean {
    return this.$refs.formRef.validate();
  }

  onSave(): void {
    if (this.formValidate()) {
      this.$emit('onSave', this.prizeRequest);
      this.revokePreviewURL();
      this.resetForm();
    }
  }

  onCancel(): void {
    this.$emit('onCancel');
    this.revokePreviewURL();
    this.resetForm();
  }

  created(): void {
    this.typeOptions = Object.keys(PRIZE_TYPES).map(pt => ({
      text: startCase(pt.toLowerCase()),
      value: pt
    }));
  }
}
